import React, { useState, useEffect, useRef, useCallback } from 'react';

export default function StrainViewer({ images, initialIndex, strainName, testDescription, onClose }) {
  const [index, setIndex] = useState(initialIndex || 0);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const touchRef = useRef({ startX: 0, startY: 0, startDist: 0, startScale: 1, moved: false });
  const lastTapRef = useRef(0);
  const containerRef = useRef(null);

  const current = images[index];

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC to close
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goNav(-1);
      if (e.key === 'ArrowRight') goNav(1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const goNav = useCallback((dir) => {
    resetZoom();
    setIndex(prev => {
      const next = prev + dir;
      if (next < 0) return images.length - 1;
      if (next >= images.length) return 0;
      return next;
    });
  }, [images.length, resetZoom]);

  // Touch/pointer for swipe & pinch
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      touchRef.current.startDist = getDistance(e.touches);
      touchRef.current.startScale = scale;
    } else if (e.touches.length === 1) {
      touchRef.current.startX = e.touches[0].clientX;
      touchRef.current.startY = e.touches[0].clientY;
      touchRef.current.moved = false;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = getDistance(e.touches);
      const newScale = Math.min(4, Math.max(1, touchRef.current.startScale * (dist / touchRef.current.startDist)));
      setScale(newScale);
      touchRef.current.moved = true;
    } else if (e.touches.length === 1 && scale > 1) {
      // Pan when zoomed
      const dx = e.touches[0].clientX - touchRef.current.startX;
      const dy = e.touches[0].clientY - touchRef.current.startY;
      setTranslate(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      touchRef.current.startX = e.touches[0].clientX;
      touchRef.current.startY = e.touches[0].clientY;
      touchRef.current.moved = true;
    } else if (e.touches.length === 1) {
      touchRef.current.moved = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length > 0) return; // still has fingers
    if (touchRef.current.moved && scale <= 1) {
      const dx = e.changedTouches[0].clientX - touchRef.current.startX;
      const dy = e.changedTouches[0].clientY - touchRef.current.startY;
      // Swipe down to close
      if (dy > 80 && Math.abs(dy) > Math.abs(dx)) {
        onClose();
        return;
      }
      // Horizontal swipe to navigate
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        goNav(dx < 0 ? 1 : -1);
        return;
      }
    }

    // Double-tap to zoom
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      if (scale > 1) {
        resetZoom();
      } else {
        setScale(2.5);
      }
    }
    lastTapRef.current = now;
  };

  // Mouse swipe (desktop)
  const mouseRef = useRef({ down: false, startX: 0, startY: 0 });

  const handleMouseDown = (e) => {
    mouseRef.current = { down: true, startX: e.clientX, startY: e.clientY };
  };

  const handleMouseUp = (e) => {
    if (!mouseRef.current.down) return;
    mouseRef.current.down = false;
    const dx = e.clientX - mouseRef.current.startX;
    const dy = e.clientY - mouseRef.current.startY;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      goNav(dx < 0 ? 1 : -1);
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.95)', display: 'flex', flexDirection: 'column',
        touchAction: 'none',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', flexShrink: 0,
      }}>
        <div style={{
          color: '#fff', fontSize: 16, fontWeight: 700,
          textShadow: '0 1px 4px rgba(0,0,0,0.5)',
        }}>
          {strainName}
          {images.length > 1 && (
            <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 8, opacity: 0.7 }}>
              {index + 1}/{images.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
            borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
            fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          &times;
        </button>
      </div>

      {/* Image */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', position: 'relative',
      }}>
        <img
          src={current.url}
          alt={strainName}
          draggable={false}
          style={{
            maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
            transition: scale === 1 ? 'transform 0.2s ease' : 'none',
            userSelect: 'none',
          }}
        />

        {/* Nav arrows for desktop */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goNav(-1); }}
              style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
                borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: 22,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              &#8249;
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goNav(1); }}
              style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
                borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: 22,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              &#8250;
            </button>
          </>
        )}
      </div>

      {/* Description */}
      {testDescription && (
        <div style={{
          padding: '12px 16px', flexShrink: 0,
          background: 'rgba(0,0,0,0.5)',
        }}>
          <div style={{
            color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.5,
            maxHeight: 80, overflowY: 'auto',
          }}>
            {testDescription}
          </div>
        </div>
      )}
    </div>
  );
}
